/**
 * Dashboard Page
 *
 * Main dashboard with real-time metrics from APIs
 */

import { Link } from 'react-router-dom'
import { nombreParaMostrar } from '@/domain/user/types'
import {
  Users,
  FileImage,
  ClipboardList,
  FileText,
  Activity,
  TrendingUp,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Eye,
  Mic,
  Send,
  Undo2,
} from 'lucide-react'

import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/application/auth'
import { usePatients } from '@/application/patients'
import { useStudies } from '@/application/studies'
import { useReports, getTranscriptionQueue, getAwaitingReview } from '@/application/reports'
import { useMyWorklist, useOverdueItems, useUnassignedItems, useReturnedItems, isWorklistItemOverdue } from '@/application/worklist'
import { Card, CardHeader, CardTitle, CardContent } from '@/presentation/components/ui/Card'
import { Badge } from '@/presentation/components/ui/Badge'
import { Button } from '@/presentation/components/ui/Button'
import { cn } from '@/lib/utils'
import { WORKLIST_STATUS_LABELS, PRIORITY_LABELS, USER_ROLES, type PriorityLevel } from '@/config/constants'
import { UnassignedWorklistCard } from '@/presentation/components/dashboard/UnassignedWorklistCard'
import { RadiologistWorkloadCard } from '@/presentation/components/dashboard/RadiologistWorkloadCard'
import { ModalityPieChart } from '@/presentation/components/dashboard/ModalityPieChart'
import { StudiesTimelineChart } from '@/presentation/components/dashboard/StudiesTimelineChart'
import { TatMetricsCard } from '@/presentation/components/dashboard/TatMetricsCard'
import { ProductivityTable } from '@/presentation/components/dashboard/ProductivityTable'
import { PersonalModalityPieChart } from '@/presentation/components/dashboard/PersonalModalityPieChart'
import { PersonalProductivityChart } from '@/presentation/components/dashboard/PersonalProductivityChart'
import { getDashboardConfig } from '@/config/dashboard.config'
import { aFechaSegura } from '@/lib/utils'

// =============================================================================
// Types
// =============================================================================

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  loading?: boolean
  change?: string
  changeType?: 'positive' | 'negative' | 'neutral'
  description?: string
  linkTo?: string
}

// =============================================================================
// Components
// =============================================================================

function StatCard({
  title,
  value,
  icon,
  loading,
  change,
  changeType = 'neutral',
  description,
  linkTo,
}: StatCardProps) {
  const content = (
    <Card className={linkTo ? 'hover:border-[var(--primary)] transition-colors cursor-pointer' : ''}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-[var(--foreground-muted)]">{title}</p>
            {loading ? (
              <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
            ) : (
              <p className="text-2xl font-bold text-[var(--foreground)]">{value}</p>
            )}
            {change && (
              <p
                className={cn(
                  'text-xs font-medium',
                  changeType === 'positive' && 'text-green-600 dark:text-green-400',
                  changeType === 'negative' && 'text-red-600 dark:text-red-400',
                  changeType === 'neutral' && 'text-[var(--foreground-muted)]'
                )}
              >
                {change}
              </p>
            )}
            {description && (
              <p className="text-xs text-[var(--foreground-muted)]">{description}</p>
            )}
          </div>
          <div className="p-3 rounded-lg bg-[var(--primary)]/10 text-[var(--primary)]">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  )

  if (linkTo) {
    return <Link to={linkTo}>{content}</Link>
  }

  return content
}

function PendingItemsAlert({ count, overdueCount }: { count: number; overdueCount: number }) {
  if (count === 0) return null

  const isUrgent = overdueCount > 0

  return (
    <Card
      className={cn(
        isUrgent
          ? 'border-red-300 bg-red-50 dark:bg-red-950/40'
          : 'border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5'
      )}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className={cn(
                'p-3 rounded-lg',
                isUrgent
                  ? 'bg-red-200 text-red-600 dark:text-red-400'
                  : 'bg-[var(--color-warning)]/20 text-[var(--color-warning)]'
              )}
            >
              {isUrgent ? (
                <AlertTriangle className="h-6 w-6" />
              ) : (
                <AlertCircle className="h-6 w-6" />
              )}
            </div>
            <div>
              <p className="font-semibold text-[var(--foreground)]">
                {count} estudios en tu lista de trabajo
              </p>
              <p className="text-sm text-[var(--foreground-muted)]">
                {overdueCount > 0 ? (
                  <span className="text-red-600 dark:text-red-400 font-medium">
                    {overdueCount} vencidos - requieren atención inmediata
                  </span>
                ) : (
                  'Pendientes de revisión'
                )}
              </p>
            </div>
          </div>
          <Link to="/worklist">
            <Button variant={isUrgent ? 'destructive' : 'default'}>
              Ver Lista
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

function MyWorklistPreview() {
  const { data: myItems, isLoading } = useMyWorklist()

  const pendingItems = myItems?.filter(
    (item) => item.status === 'PENDING' || item.status === 'IN_PROGRESS'
  ) || []

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Mi Lista de Trabajo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <ClipboardList className="h-5 w-5" />
          Mi Lista de Trabajo
        </CardTitle>
        <Link to="/worklist">
          <Button variant="ghost" size="sm">
            Ver todo
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {pendingItems.length === 0 ? (
          <p className="text-sm text-[var(--foreground-muted)] text-center py-4">
            No tienes estudios pendientes
          </p>
        ) : (
          <div className="space-y-3">
            {pendingItems.slice(0, 5).map((item) => {
              const isOverdue = isWorklistItemOverdue(item)
              return (
                <div
                  key={item.id}
                  className={cn(
                    'flex items-center justify-between p-3 rounded-lg',
                    isOverdue ? 'bg-red-50 dark:bg-red-950/40' : 'bg-[var(--background-subtle)]'
                  )}
                >
                  {/*
                    Que se vea DE QUE estudio se trata.

                    Esta lista solo mostraba la prioridad y el estado: cinco
                    filas que decian "Rutina / En Lectura" y nada mas. Para
                    elegir por donde empezar, el radiologo necesita saber de
                    quien es el estudio y que estudio es; si no, tiene que
                    abrirlos uno por uno. El dato ya venia en la respuesta
                    (`patientName`, `modality`, `studyDescription`), solo que
                    no se pintaba.
                  */}
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge
                      variant={
                        item.priority === 1
                          ? 'destructive'
                          : item.priority === 2
                          ? 'warning'
                          : 'secondary'
                      }
                    >
                      {PRIORITY_LABELS[item.priority as PriorityLevel]}
                    </Badge>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {item.studyDescription || 'Estudio sin descripción'}
                      </p>
                      <p className="text-xs text-[var(--foreground-muted)] truncate">
                        {item.patientName} · {item.modality} ·{' '}
                        {WORKLIST_STATUS_LABELS[item.status]}
                      </p>
                    </div>
                    {isOverdue && (
                      <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                    )}
                  </div>
                  <Link to={`/viewer/${item.studyId}`}>
                    <Button variant="ghost" size="sm">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              )
            })}
            {pendingItems.length > 5 && (
              <p className="text-xs text-[var(--foreground-muted)] text-center">
                +{pendingItems.length - 5} más
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function RecentStudies() {
  const { data, isLoading } = useStudies({ limit: 5 })

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Estudios Recientes
        </CardTitle>
        <Link to="/studies">
          <Button variant="ghost" size="sm">
            Ver todos
          </Button>
        </Link>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
          </div>
        ) : !data?.studies.length ? (
          <p className="text-sm text-[var(--foreground-muted)] text-center py-4">
            No hay estudios recientes
          </p>
        ) : (
          <div className="space-y-4">
            {data.studies.map((study) => (
              <div
                key={study.id}
                className="flex items-start gap-3 pb-3 border-b border-[var(--border)] last:border-0 last:pb-0"
              >
                <div className="w-2 h-2 mt-2 rounded-full bg-[var(--primary)]" />
                <div className="flex-1">
                  <Link
                    to={`/studies/${study.id}`}
                    className="text-sm text-[var(--foreground)] hover:text-[var(--primary)]"
                  >
                    {study.studyDescription || 'Estudio sin descripción'}
                  </Link>
                  <p className="text-xs text-[var(--foreground-muted)]">
                    {study.modality} • {aFechaSegura(study.studyDate).toLocaleDateString('es-CO')}
                  </p>
                </div>
                <Link to={`/viewer/${study.id}`}>
                  <Button variant="ghost" size="sm">
                    <Eye className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function QuickStats({ canViewReports = true }: { canViewReports?: boolean }) {
  const { data: reportsData, isLoading: reportsLoading } = useReports({ limit: 100, enabled: canViewReports })
  const { data: overdueItems } = useOverdueItems()
  const { data: studiesData } = useStudies({ limit: 1 })

  /**
   * Informes firmados hoy, contados por la fecha de FIRMA.
   *
   * Antes se filtraba por `createdAt`: un informe empezado ayer y firmado esta
   * manana no contaba, y uno creado hoy y sin firmar tampoco, aunque el titulo
   * dijera "firmados hoy". Con los estudios repartidos en el tiempo, que es
   * como llegan de verdad, el numero era simplemente falso.
   *
   * Se cuentan tambien las enmiendas: una enmienda es un informe nuevo ya
   * firmado, y el especialista hizo el mismo acto.
   */
  const esDeHoy = (fecha: string | null): boolean => {
    if (!fecha) return false
    return new Date(fecha).toDateString() === new Date().toDateString()
  }

  const signedToday =
    reportsData?.reports.filter(
      (r) => (r.status === 'FINAL' || r.status === 'AMENDED') && esDeHoy(r.signedAt)
    ).length || 0
  const pendingReports = reportsData?.reports.filter(
    (r) => r.status === 'DRAFT' || r.status === 'PRELIMINARY'
  ).length || 0

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Resumen de Hoy
        </CardTitle>
      </CardHeader>
      <CardContent>
        {canViewReports && reportsLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
          </div>
        ) : (
          <div className="space-y-4">
            {canViewReports && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[var(--foreground-muted)]">
                    Informes firmados hoy en la IPS
                  </span>
                  <span className="font-semibold text-green-600 dark:text-green-400">{signedToday}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[var(--foreground-muted)]">
                    Reportes pendientes
                  </span>
                  <span className="font-semibold">{pendingReports}</span>
                </div>
              </>
            )}
            <div className="flex justify-between items-center">
              <span className="text-sm text-[var(--foreground-muted)]">
                Estudios vencidos
              </span>
              <span
                className={cn(
                  'font-semibold',
                  overdueItems && overdueItems.length > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                )}
              >
                {overdueItems?.length || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-[var(--foreground-muted)]">
                Total estudios
              </span>
              <span className="font-semibold">{studiesData?.total || 0}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Transcriptor Dashboard
// =============================================================================

function TranscriptorDashboard({ user }: { user: { email?: string; role?: string } | null }) {
  // Get config from centralized dashboard config
  const transcriptorConfig = getDashboardConfig(user?.role)

  // Fetch transcription queue
  const { data: queueData, isLoading: queueLoading } = useQuery({
    queryKey: ['transcription-queue'],
    queryFn: getTranscriptionQueue,
    staleTime: 1000 * 60, // 1 minute
  })

  // Fetch awaiting review
  const { data: reviewData, isLoading: reviewLoading } = useQuery({
    queryKey: ['awaiting-review'],
    queryFn: getAwaitingReview,
    staleTime: 1000 * 60,
  })

  const pendingCount = queueData?.reports.filter(r => r.status === 'DRAFT').length || 0
  const inProgressCount = queueData?.reports.filter(r => r.status === 'TRANSCRIBING').length || 0
  const awaitingReviewCount = reviewData?.reports.length || 0

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome Message */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--foreground)]">
          Bienvenido, {nombreParaMostrar(user)}
        </h1>
        <p className="text-[var(--foreground-muted)]">
          {transcriptorConfig.welcome.subtitle}
        </p>
      </div>

      {/* Alert for pending items */}
      {pendingCount > 0 && (
        <Card className="border-[var(--color-warning)]/50 bg-[var(--color-warning)]/5">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-[var(--color-warning)]/20 text-[var(--color-warning)]">
                  <Mic className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-semibold text-[var(--foreground)]">
                    {pendingCount} reportes pendientes de transcribir
                  </p>
                  <p className="text-sm text-[var(--foreground-muted)]">
                    Haz clic para ver la cola
                  </p>
                </div>
              </div>
              <Link to="/transcription">
                <Button>Ver Cola</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Pendientes"
          value={pendingCount}
          icon={<Mic className="h-5 w-5" />}
          loading={queueLoading}
          description="Por transcribir"
          linkTo="/transcription"
        />
        <StatCard
          title="En Progreso"
          value={inProgressCount}
          icon={<Activity className="h-5 w-5" />}
          loading={queueLoading}
          description="Transcribiendo"
          linkTo="/transcription"
        />
        <StatCard
          title="En Revisión"
          value={awaitingReviewCount}
          icon={<Send className="h-5 w-5" />}
          loading={reviewLoading}
          description="Enviados a radiólogo"
        />
      </div>

      {/* Personal Charts - Transcriptor productivity */}
      {(transcriptorConfig.charts.showTimelineChart || transcriptorConfig.charts.showModalityChart) && (
        <div className="grid gap-6 lg:grid-cols-2">
          {transcriptorConfig.charts.showTimelineChart && <PersonalProductivityChart days={30} />}
          {transcriptorConfig.charts.showModalityChart && <PersonalModalityPieChart />}
        </div>
      )}

      {/* Queue Preview */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Mic className="h-5 w-5" />
            Cola de Transcripcion
          </CardTitle>
          <Link to="/transcription">
            <Button variant="ghost" size="sm">
              Ver todo
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {queueLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
            </div>
          ) : !queueData?.reports.length ? (
            <p className="text-sm text-[var(--foreground-muted)] text-center py-4">
              No hay reportes pendientes de transcripcion
            </p>
          ) : (
            <div className="space-y-3">
              {queueData.reports.slice(0, 5).map((report) => (
                <div
                  key={report.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-[var(--background-subtle)]"
                >
                  <div className="flex items-center gap-3">
                    <Badge
                      variant={report.status === 'TRANSCRIBING' ? 'warning' : 'secondary'}
                    >
                      {report.status === 'TRANSCRIBING' ? 'Transcribiendo' : 'Pendiente'}
                    </Badge>
                    <span className="text-sm truncate max-w-[200px]">
                      {report.title || 'Sin titulo'}
                    </span>
                  </div>
                  <Link to={`/transcription/${report.id}`}>
                    <Button variant="ghost" size="sm">
                      <Mic className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              ))}
              {queueData.reports.length > 5 && (
                <p className="text-xs text-[var(--foreground-muted)] text-center">
                  +{queueData.reports.length - 5} mas
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function DashboardPage() {
  const { user } = useAuth()

  // Check if user can manage assignments (admin/technician)
  const isAdminOrTechnician =
    user?.role === USER_ROLES.ADMIN ||
    user?.role === USER_ROLES.SUPER_ADMIN ||
    user?.role === USER_ROLES.TECHNICIAN

  // Check if user is a radiologist
  const isRadiologist = user?.role === USER_ROLES.RADIOLOGIST

  // Check if user is a transcriptor
  const isTranscriptor = user?.role === USER_ROLES.TRANSCRIPTOR

  // Check if user can view reports (not TECHNICIAN or TRANSCRIPTOR)
  const canViewReports =
    user?.role === USER_ROLES.ADMIN ||
    user?.role === USER_ROLES.SUPER_ADMIN ||
    user?.role === USER_ROLES.RADIOLOGIST ||
    user?.role === USER_ROLES.DOCTOR

  // Fetch data - only fetch what the user has permission to see
  const { data: patientsData, isLoading: patientsLoading } = usePatients({ limit: 1 })
  const { data: studiesData, isLoading: studiesLoading } = useStudies({ limit: 1 })
  const { data: reportsData, isLoading: reportsLoading } = useReports({ limit: 1, enabled: canViewReports })
  const { data: myWorklist } = useMyWorklist()
  const { data: overdueItems } = useOverdueItems()
  const { data: unassignedItems } = useUnassignedItems({ enabled: isAdminOrTechnician })
  const { data: returnedItems } = useReturnedItems()

  const pendingCount = myWorklist?.filter(
    (item) => item.status === 'PENDING' || item.status === 'IN_PROGRESS'
  ).length || 0

  const overdueCount = overdueItems?.length || 0
  const unassignedCount = unassignedItems?.length || 0
  const returnedCount = returnedItems?.length || 0

  // Radiologist-specific dashboard
  if (isRadiologist) {
    const radiologistConfig = getDashboardConfig(user?.role)

    return (
      <div className="space-y-6 animate-fade-in">
        {/* Welcome Message */}
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]">
            Bienvenido, {nombreParaMostrar(user)}
          </h1>
          <p className="text-[var(--foreground-muted)]">
            {radiologistConfig.welcome.subtitle}
          </p>
        </div>

        {/* Pending Items Alert */}
        <PendingItemsAlert count={pendingCount} overdueCount={overdueCount} />

        {/* Stats Grid - Radiologist specific */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title={`${WORKLIST_STATUS_LABELS.PENDING}s`}
            value={myWorklist?.filter(i => i.status === 'PENDING').length || 0}
            icon={<ClipboardList className="h-5 w-5" />}
            linkTo="/worklist"
          />
          <StatCard
            title={WORKLIST_STATUS_LABELS.IN_PROGRESS}
            value={myWorklist?.filter(i => i.status === 'IN_PROGRESS').length || 0}
            icon={<Activity className="h-5 w-5" />}
            linkTo="/worklist"
          />
          <StatCard
            title="Completados por mí hoy"
            value={myWorklist?.filter(i => {
              if (i.status !== 'COMPLETED' || !i.updatedAt) return false
              const today = new Date()
              const updated = new Date(i.updatedAt)
              return updated.toDateString() === today.toDateString()
            }).length || 0}
            icon={<FileText className="h-5 w-5" />}
          />
          <StatCard
            title="Vencidos"
            value={overdueCount}
            icon={<AlertTriangle className="h-5 w-5" />}
            description={overdueCount > 0 ? 'Requieren atencion' : 'Todo al dia'}
            linkTo="/worklist"
          />
        </div>

        {/* Personal Charts - Radiologist productivity */}
        {(radiologistConfig.charts.showTimelineChart || radiologistConfig.charts.showModalityChart) && (
          <div className="grid gap-6 lg:grid-cols-2">
            {radiologistConfig.charts.showTimelineChart && <PersonalProductivityChart days={30} />}
            {radiologistConfig.charts.showModalityChart && <PersonalModalityPieChart />}
          </div>
        )}

        {/* My Worklist */}
        <div className="grid gap-6 lg:grid-cols-2">
          <MyWorklistPreview />
          <QuickStats />
        </div>
      </div>
    )
  }

  // Transcriptor-specific dashboard
  if (isTranscriptor) {
    return <TranscriptorDashboard user={user} />
  }

  // Get config from centralized dashboard config
  const adminConfig = getDashboardConfig(user?.role)

  // Admin/Technician dashboard (default)
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome Message */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--foreground)]">
          Bienvenido, {nombreParaMostrar(user)}
        </h1>
        <p className="text-[var(--foreground-muted)]">
          {adminConfig.welcome.subtitle}
        </p>
      </div>

      {/* Unassigned Alert for Admin/Technician */}
      {isAdminOrTechnician && unassignedCount > 0 && (
        <Card className="border-[var(--color-warning)]/50 bg-[var(--color-warning)]/5">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-[var(--color-warning)]/20 text-[var(--color-warning)]">
                  <AlertCircle className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-semibold text-[var(--foreground)]">
                    {unassignedCount} estudios sin asignar
                  </p>
                  <p className="text-sm text-[var(--foreground-muted)]">
                    Requieren asignacion a un radiologo
                  </p>
                </div>
              </div>
              <Link to="/worklist?tab=unassigned">
                <Button>Asignar Estudios</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Returned Studies Alert for Admin/Technician */}
      {isAdminOrTechnician && returnedCount > 0 && (
        <Card className="border-orange-300 bg-orange-50 dark:bg-orange-950/40">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-orange-200 text-orange-600 dark:text-orange-400">
                  <Undo2 className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-semibold text-[var(--foreground)]">
                    {returnedCount} estudios {WORKLIST_STATUS_LABELS.RETURNED.toLowerCase()}s
                  </p>
                  <p className="text-sm text-[var(--foreground-muted)]">
                    Requieren reasignacion a otro radiologo
                  </p>
                </div>
              </div>
              <Link to="/worklist?tab=returned">
                <Button variant="outline">Ver Devueltos</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className={cn(
        'grid gap-4 md:grid-cols-2',
        canViewReports ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
      )}>
        <StatCard
          title="Pacientes"
          value={patientsData?.total.toLocaleString() || '0'}
          icon={<Users className="h-5 w-5" />}
          loading={patientsLoading}
          linkTo="/patients"
        />
        <StatCard
          title="Estudios"
          value={studiesData?.total.toLocaleString() || '0'}
          icon={<FileImage className="h-5 w-5" />}
          loading={studiesLoading}
          linkTo="/studies"
        />
        <StatCard
          title="Sin Asignar"
          value={unassignedCount}
          icon={<ClipboardList className="h-5 w-5" />}
          description={`${WORKLIST_STATUS_LABELS.PENDING}s de asignacion`}
          linkTo="/worklist?tab=unassigned"
        />
        {returnedCount > 0 && (
          <StatCard
            title={`${WORKLIST_STATUS_LABELS.RETURNED}s`}
            value={returnedCount}
            icon={<Undo2 className="h-5 w-5" />}
            description="Requieren reasignacion"
            linkTo="/worklist?tab=returned"
          />
        )}
        {canViewReports && (
          <StatCard
            title="Reportes"
            value={reportsData?.total.toLocaleString() || '0'}
            icon={<FileText className="h-5 w-5" />}
            loading={reportsLoading}
            linkTo="/reports"
          />
        )}
      </div>

      {/* Charts Row - Admin only */}
      {isAdminOrTechnician && (
        <div className="grid gap-6 lg:grid-cols-2">
          <StudiesTimelineChart days={30} />
          <ModalityPieChart />
        </div>
      )}

      {/* Productivity Section - Admin only */}
      {(user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.SUPER_ADMIN) && (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* `min-w-0`: por defecto una celda de rejilla no baja del ancho de
              su contenido, y aqui dentro hay una tabla. Sin esto la celda
              medía 419px en una pantalla de 375 y empujaba la página. */}
          <div className="lg:col-span-2 min-w-0">
            <ProductivityTable />
          </div>
          <TatMetricsCard />
        </div>
      )}

      {/* Secondary Grid - Admin view */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Unassigned Worklist */}
          {isAdminOrTechnician && <UnassignedWorklistCard />}

          {/* Recent Studies */}
          <RecentStudies />
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Radiologist Workload */}
          {isAdminOrTechnician && <RadiologistWorkloadCard />}

          {/* Quick Stats */}
          <QuickStats canViewReports={canViewReports} />
        </div>
      </div>
    </div>
  )
}
